import { App, Button, Empty, Form, Input, InputNumber, Select, Switch, Tag } from 'antd';
import { useMemo, useState } from 'react';
import { api, errorText } from '../../api/client';
import type { ProductDetail } from '../../api/types';

type Values = Record<string, string | number | boolean | null | undefined>;

/** Form thông số sinh từ định nghĩa của danh mục – lọc sản phẩm dựa trên dữ liệu có cấu trúc (PRD 5.2). */
export function SpecsTab({
  product,
  canEdit,
  onSaved,
}: {
  product: ProductDetail;
  canEdit: boolean;
  onSaved: (p: ProductDetail) => void;
}) {
  const { message } = App.useApp();
  const [saving, setSaving] = useState(false);
  const initial = useMemo(() => {
    const v: Values = {};
    for (const s of product.specifications) {
      v[s.spec_key] =
        s.value_number !== null ? Number(s.value_number) : (s.value_text ?? s.value_boolean);
    }
    return v;
  }, [product.specifications]);

  if (product.spec_definitions.length === 0) {
    return (
      <Empty description="Danh mục này chưa có định nghĩa thông số. Thêm ở mục 'Danh mục & thông số'." />
    );
  }

  const submit = async (values: Values) => {
    setSaving(true);
    try {
      const specifications = Object.entries(values)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([spec_key, value]) => ({ spec_key, value: value as string | number | boolean }));
      onSaved(
        await api.put<ProductDetail>(`/admin/products/${product.id}/specifications`, {
          specifications,
        }),
      );
      message.success('Đã lưu thông số');
    } catch (e) {
      message.error(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form
      layout="vertical"
      initialValues={initial}
      onFinish={submit}
      disabled={!canEdit}
      style={{ maxWidth: 640 }}
    >
      {product.spec_definitions.map((d) => (
        <Form.Item
          key={d.id}
          name={d.spec_key}
          label={
            <>
              {d.label}
              {d.unit ? ` (${d.unit})` : ''}
              {d.is_filterable && <Tag style={{ marginLeft: 8 }}>dùng để lọc</Tag>}
            </>
          }
          rules={d.is_required ? [{ required: true, message: 'Bắt buộc để xuất bản' }] : []}
          valuePropName={d.data_type === 'boolean' ? 'checked' : 'value'}
        >
          {d.data_type === 'number' ? (
            <InputNumber
              style={{ width: 220 }}
              min={0}
              decimalSeparator=","
              suffix={d.unit ?? undefined}
            />
          ) : d.data_type === 'option' ? (
            <Select
              allowClear
              style={{ width: 220 }}
              options={(d.options ?? []).map((o) => ({ value: o.value, label: o.label }))}
            />
          ) : d.data_type === 'boolean' ? (
            <Switch />
          ) : (
            <Input />
          )}
        </Form.Item>
      ))}
      {canEdit && (
        <Button type="primary" htmlType="submit" loading={saving}>
          Lưu thông số
        </Button>
      )}
    </Form>
  );
}
